/**
 * LevelChip Component
 * 
 * A reusable component for displaying learner levels with consistent styling
 * across the client application. Shows level with appropriate colors and icons.
 * 
 * Used in: Content cards and detail views throughout the application
 */
import React from 'react';
import { Chip, Box } from '@mui/material';
import { 
    School as BeginnerIcon,
    MenuBook as IntermediateIcon,
    AutoStories as AdvancedIcon,
    MenuBook
} from '@mui/icons-material';
import PropTypes from 'prop-types';

const LevelChip = ({ level, variant = 'filled', size = 'small', showIcon = true }) => {
    if (!level || !level.name) {
        return null;
    }

    const getLevelConfig = (levelName) => {
        switch (levelName.toLowerCase()) {
            case 'beginner':
                return {
                    color: 'success',
                    icon: <BeginnerIcon sx={{ fontSize: 16 }} />,
                    bgColor: '#e8f5e8',
                    textColor: '#2e7d32'
                };
            case 'intermediate':
                return {
                    color: 'warning',
                    icon: <IntermediateIcon sx={{ fontSize: 16 }} />,
                    bgColor: '#fff3e0',
                    textColor: '#f57c00'
                };
            case 'advanced':
                return {
                    color: 'error',
                    icon: <AdvancedIcon sx={{ fontSize: 16 }} />,
                    bgColor: '#ffebee',
                    textColor: '#d32f2f'
                };
            default:
                return {
                    color: 'default',
                    icon: <MenuBook sx={{ fontSize: 16 }} />,
                    bgColor: '#f5f5f5',
                    textColor: '#666'
                };
        }
    };

    const config = getLevelConfig(level.name);

    if (variant === 'text') {
        return (
            <Box 
                sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    color: config.textColor,
                    fontSize: size === 'small' ? '0.75rem' : '0.875rem'
                }}
            >
                {showIcon && config.icon}
                {level.name}
            </Box>
        );
    }

    return (
        <Chip
            icon={showIcon ? config.icon : undefined}
            label={`${level.name}`}
            variant={variant}
            color={config.color}
            size={size}
            sx={{
                fontSize: size === 'small' ? '0.7rem' : '0.75rem',
                height: size === 'small' ? '24px' : '28px',
                fontWeight: 500,
                ...(variant === 'filled' && {
                    backgroundColor: config.bgColor,
                    color: config.textColor,
                    '& .MuiChip-icon': {
                        color: config.textColor
                    }
                })
            }}
        />
    );
};

LevelChip.propTypes = {
    level: PropTypes.shape({
        _id: PropTypes.string,
        name: PropTypes.string.isRequired
    }),
    variant: PropTypes.oneOf(['filled', 'outlined', 'text']),
    size: PropTypes.oneOf(['small', 'medium']),
    showIcon: PropTypes.bool
};

export default LevelChip;
